import {getComboMarkdownEditor, initComboMarkdownEditor} from './comp/ComboMarkdownEditor.ts';
import {GET} from '../modules/fetch.ts';

/**
 * Returns the comment Element for creating a new thread, not replying to an
 * existing thread.  Returns null if not found.
 * @param {HTMLElement} parent
 * @return {Element | null}
 */
function findNewCommentThreadElement(parent: HTMLElement): Element | null {
  const elems = parent.querySelectorAll('.add-comment');
  if (elems.length === 0) return null;
  // The form for a new comment thread, not replying to an existing thread is
  // always the last one.  But it also doesn't have a .comment-list of
  // existing comments inside it.
  for (let i = elems.length - 1; i >= 0; i--) {
    const candidate = elems.item(i);
    if (!candidate) continue;
    const commentList = candidate.querySelector('.comment-list');
    if (!commentList) return candidate;
  }
  return null;
}

/**
 * @param {HTMLElement} element
 * @param {Element | null} [newCommentThread=null]
 * @return {{textarea: HTMLTextAreaElement | null, editor: ComboMarkdownEditor | null}}
 */
export function findCommentForm(element: HTMLElement, newCommentThread: Element | null = null) {
  if (!newCommentThread) {
    newCommentThread = findNewCommentThreadElement(element);
    if (!newCommentThread) {
      return {textarea: null, editor: null};
    }
  }

  /** @type {HTMLTextAreaElement | null} */
  const textarea = newCommentThread.querySelector('.comment-code-cloud textarea');
  if (!textarea) throw new Error(`Couldn't find textarea in comment form; newCommentThread.id=${newCommentThread?.id}, newCommentThread.className=${newCommentThread?.className}`);
  /** @type {ComboMarkdownEditor | null} */
  let editor = null;
  if ('_giteaComboMarkdownEditor' in textarea) {
    editor = textarea._giteaComboMarkdownEditor;
  }
  return {textarea, editor};
}

/**
 * Create the form and insert it into the DOM if it doesn't already exit.
 * Always update the line input field.
 *
 * This mimics code in initPullRequestReview() for creating a comment form.
 *
 * Returns the editor object and the textrea of the content.
 *
 * @param {HTMLElement} parent
 * @param {string | null} lineString
 * @return {Promise<{editor: ComboMarkdownEditor, textarea: HTMLTextAreaElement | null}>}
 */
export async function ensureCommentFormExists(parent: HTMLElement, lineString: string | null) {
  // Always create a new thread.  We may want to change this in the future.
  let comment = findNewCommentThreadElement(parent);
  if (!comment) {
    comment = document.createElement('div');
    comment.className = 'add-comment';
    const selector = '.code-conversations';
    const insertionElem = parent.querySelector(selector);
    if (!insertionElem) {
      throw new Error(`Couldn't find place to insert comment form; looked for selector: ${selector}; parent.id=${parent?.id}, parent.className=${parent?.className}`);
    }
    insertionElem.append(comment);
  }

  let commentCloud = comment.querySelector('.comment-code-cloud');
  /** @type {HTMLTextAreaElement | null} */
  let textarea = null;
  let editor = null;
  if (!commentCloud) {
    const path = parent.getAttribute('data-path') || '';
    const subPath = parent.getAttribute('data-sub-path') || '';
    const res = await GET(parent.getAttribute('data-new-comment-url'));
    const html = await res.text();
    comment.innerHTML = html;
    commentCloud = comment.querySelector('.comment-code-cloud');
    if (!commentCloud) {
      throw new Error(`Couldn't find .comment-code-cloud in comment form; parent.id=${parent?.id}, parent.className=${parent?.className}`);
    }
    const menuElem = commentCloud.querySelector('.menu');
    if (!menuElem) {
      throw new Error(`Couldn't find .menu in comment form; parent.id=${parent?.id}, parent.className=${parent?.className}`);
    }
    const sideInput = comment.querySelector<HTMLInputElement>("input[name='side']");
    // TODO: What should the side be?  Options are: previous, proposed.
    if (sideInput) sideInput.value = 'proposed';
    const pathInput = comment.querySelector<HTMLInputElement>("input[name='path']");
    if (pathInput) pathInput.value = path;
    const subPathInput = comment.querySelector<HTMLInputElement>("input[name='sub_path']");
    if (subPathInput) subPathInput.value = subPath;

    textarea = commentCloud.querySelector('textarea');
    editor = getComboMarkdownEditor(textarea);
    if (!editor) {
      const formElem = commentCloud.querySelector('.form');
      if (!formElem) {
        throw new Error(`Couldn't find .form in comment form; parent.id=${parent?.id}, parent.className=${parent?.className}`);
      }
      editor = await initComboMarkdownEditor(formElem.querySelector('.combo-markdown-editor'));
    }
  }
  // Update the coordinates.
  const lineInput = comment.querySelector<HTMLInputElement>("input[name='line']");
  if (lineInput) lineInput.value = lineString ?? '';

  if (editor) {
    return {textarea, editor};
  }

  const findResult = findCommentForm(parent, comment);
  // We need to destructure this so that the type-checker can see that we've
  // ruled out null.
  textarea = findResult.textarea;
  editor = findResult.editor;
  if (!editor) throw new Error(`Couldn't find editor in comment form; comment.id=${comment?.id}, comment.className=${comment?.className}`);
  return {textarea, editor};
}

let cachedUseAnimation: boolean | undefined;
/**
 * Returns true if the user wants to see animations.
 *
 * You generally want to force the check so that if the preference gets updated,
 * it will take effect.  But if you're calling this in a loop, maybe not.
 * @param {boolean} forceCheck
 * @return {boolean}
 */
function useAnimation(forceCheck: boolean): boolean {
  if (!forceCheck && cachedUseAnimation !== undefined) return cachedUseAnimation;

  // For accessibility, check the user's preference.
  const mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)');
  cachedUseAnimation = !mediaQueryList.matches;
  return cachedUseAnimation;
}

/**
 * Returns a Promise that resolves when scrolling of the given element finishes.
 * Since browsers don't have native support for this, it's an approximation.
 * @param {Document | Element | Window} [scroller=window]
 * @return {Promise<void>}
 */
function detectEndOfScrolling(scroller = window) {
  return new Promise<void>((resolve) => {
    let timerId: ReturnType<typeof setTimeout> | null = null;
    const eventOptions: AddEventListenerOptions = {passive: true};
    let timeoutHandler: () => void;
    const scrollHandler = () => {
      if (timerId === null) return;
      clearTimeout(timerId);

      timerId = setTimeout(timeoutHandler, 300);
    };
    timeoutHandler = () => {
      // Clean up.
      scroller.removeEventListener('scroll', scrollHandler, eventOptions);
      timerId = null;

      resolve();
    };
    // eslint-disable-next-line github/prefer-observers
    scroller.addEventListener('scroll', scrollHandler, eventOptions);

    timerId = setTimeout(timeoutHandler, 300);
  });
}

/**
 * Returns a Promise that resolves when scrolling finishes.
 * @param {Element} elem
 * @param {ScrollIntoViewOptions} [options={}]
 */
export function scrollIntoView(elem: Element, options: ScrollIntoViewOptions = {}) {
  if (useAnimation(true)) {
    // Use smooth scrolling.
    options.behavior = 'smooth';
  }

  try {
    elem.scrollIntoView(options);
  } catch {
    // Safari doesn't support options.
    elem.scrollIntoView();
  }

  return detectEndOfScrolling();
}
